// components/EditPortsOrderModal.jsx
import React, { useState } from "react";
import { X, Loader } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const EditPortsOrderModal = ({ portsOrder, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    portsOrderName: portsOrder.portsOrderName || "",
    numbersToPort: portsOrder.numbersToPort || "",
    requestedDate: portsOrder.requestedDate
      ? portsOrder.requestedDate.split("T")[0]
      : "",
    acceptedDate: portsOrder.acceptedDate
      ? portsOrder.acceptedDate.split("T")[0]
      : "",
    portDate: portsOrder.portDate ? portsOrder.portDate.split("T")[0] : "",
    portCompleted: portsOrder.portCompleted || false,
    notes: portsOrder.notes || "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Create cleaned data object with null for empty values
      const cleanedData = {
        portsOrderName: formData.portsOrderName.trim() || null,
        numbersToPort: formData.numbersToPort.trim() || null,
        requestedDate: formData.requestedDate || null,
        acceptedDate: formData.acceptedDate || null,
        portDate: formData.portDate || null,
        portCompleted: formData.portCompleted,
        notes: formData.notes.trim() || null,
      };

      await apiService.updatePortsOrder(portsOrder.id, cleanedData);
      onSuccess();
    } catch (err) {
      console.error("Error updating ports order:", err);
      setError(err.response?.data?.message || "Failed to update ports order");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border border-gray-700"
            : "bg-white border border-gray-300"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Edit Ports Order
          </h2>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-6">
          <form
            onSubmit={handleSubmit}
            id="portsOrderForm"
            className="space-y-6"
          >
            {error && (
              <div
                className={`p-3 rounded-lg text-sm ${
                  isDarkMode
                    ? "bg-red-500/20 border border-red-500/30 text-red-400"
                    : "bg-red-50 border border-red-200 text-red-600"
                }`}
              >
                {error}
              </div>
            )}

            {/* Basic Information */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Basic Information
              </h3>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Ports Order Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="portsOrderName"
                  value={formData.portsOrderName}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                  placeholder="Enter ports order name"
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Numbers to Port
                </label>
                <textarea
                  name="numbersToPort"
                  value={formData.numbersToPort}
                  onChange={handleChange}
                  rows={3}
                  className={`w-full px-4 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                  }`}
                  placeholder="Enter phone numbers to port (one per line or comma-separated)"
                />
                <div
                  className={`text-xs mt-1 ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  Leave empty to clear numbers
                </div>
              </div>
            </div>

            {/* Dates */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Dates
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Requested Date
                </label>
                <input
                  type="date"
                  name="requestedDate"
                  value={formData.requestedDate}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Accepted Date
                </label>
                <input
                  type="date"
                  name="acceptedDate"
                  value={formData.acceptedDate}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Port Date
                </label>
                <input
                  type="date"
                  name="portDate"
                  value={formData.portDate}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#0C0E12] border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>

            {/* Status */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Status
              </h3>
            </div>

            <div className="mb-6">
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  name="portCompleted"
                  checked={formData.portCompleted}
                  onChange={handleChange}
                  className={`w-4 h-4 rounded border focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-700 bg-[#0C0E12] text-blue-600"
                      : "border-gray-300 bg-white text-blue-600"
                  }`}
                />
                Port Completed
              </label>
            </div>

            {/* Notes */}
            <div
              className={`px-4 py-2 mb-4 rounded ${
                isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
              }`}
            >
              <h3
                className={`text-sm font-semibold ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Additional Information
              </h3>
            </div>

            <div className="mb-6">
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className={`w-full px-4 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                placeholder="Additional notes..."
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${
            isDarkMode
              ? "border-gray-700 bg-[#0C0E12]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium border rounded transition-colors ${
              isDarkMode
                ? "text-gray-300 bg-[#13161B] border-gray-700 hover:bg-gray-700"
                : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="portsOrderForm"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && <Loader size={16} className="animate-spin" />}
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPortsOrderModal;
